export type SearchGroupType = 'SERVICES' | 'FOOD' | 'PHARMACY' | 'ADOPTION';

/**
 * One result, whatever it is — a vendor, a product or a pet.
 *
 * Deliberately one shape for all four groups so the list has one renderer.
 * `target` says where a tap goes; never branch on `type` for navigation, or the
 * app has to know that FOOD opens a product sheet while SERVICES opens a vendor.
 */
export type SearchHit = {
  type: SearchGroupType;
  id: string;
  title: string;
  subtitle: string | null;
  /** Why this matched when the title doesn't explain it — "Offers Dental cleaning". */
  matched_on: string | null;
  price_inr: string | null;
  /** Only set when it is genuinely above the price — safe to strike through. */
  mrp_inr: string | null;
  discount_pct: number | null;
  image: { id: string; thumb_path: string; full_path: string } | null;
  /** Ready-to-render chips, most important first: "Prescription needed", "Vaccinated". */
  badges: string[];
  distance_km: number | null;
  rating: number | null;
  review_count: number | null;
  target: {
    kind: 'VENDOR' | 'PRODUCT' | 'ADOPTION_LISTING';
    id: string;
    vertical?: 'FOOD' | 'PHARMACY';
  };
};

export type SearchGroup = {
  type: SearchGroupType;
  label: string;
  /** All matches, not just the ones returned — drives "See all N". */
  total: number;
  items: SearchHit[];
};

export type SearchResponse = {
  query: string;
  total: number;
  groups: SearchGroup[];
};

/** Where a hit leads. The server tells us the kind; this maps it to our routes. */
export function route_for(hit: SearchHit): string {
  switch (hit.target.kind) {
    case 'VENDOR':
      return `/app/discover/${hit.target.id}`;
    case 'ADOPTION_LISTING':
      return `/app/adopt/${hit.target.id}`;
    case 'PRODUCT':
      // The Shop is a browse screen rather than a per-product route, so land on
      // the right tab with the product's name pre-searched.
      return `/app/shop?vertical=${(hit.target.vertical ?? 'FOOD').toLowerCase()}&q=${encodeURIComponent(hit.title)}`;
  }
}
